<template>
    <div class="relative min-h-screen w-full flex bg-gradient-to-br from-gray-100 to-blue-50">

        <!-- Toggle Filter Button (top-left corner) -->
        <button @click="toggleFilter"
            class="fixed top-24 left-4 z-50 bg-white shadow-md rounded-full p-2 hover:bg-gray-100 h-12 w-12 flex flex-col justify-center items-center">
            <font-awesome-icon :icon="['fas', 'filter']" class="text-gray-700 text-xl" />
        </button>

        <!-- Sliding Sidebar Filter -->
        <transition name="slide">
            <aside v-show="showFilter"
                class="fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-40 flex flex-col border-r">
                <div class="p-6 border-b">
                    <h2 class="text-xl font-bold text-gray-800 w-full flex items-center justify-center pt-20 mt-1">
                        <font-awesome-icon :icon="['fas', 'sliders-h']" class="mr-2 text-blue-600" />
                        Filters
                    </h2>
                </div>
                <div class="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Entity</label>
                        <select v-model="filters.entity" class="w-full px-2 py-1 border outline-none">
                            <option value="ADPOLY">ADPOLY</option>
                            <option value="FCHS">FCHS</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Campus</label>
                        <div class="border px-4 py-2 h-auto flex items-center relative cursor-pointer"
                            :class="activeDropDown == 'campus' ? 'z-30' : ''">
                            <div class="flex flex-grow justify-between items-center"
                                @click.stop="showCampusDropDown = !showCampusDropDown">
                                <div class="text-sm mr-2 sm:mb-0 flex-shrink-0">All</div>
                                <icon-chevron-down
                                    class="h-4 w-4 transform transition-transform duration-300 ease-in-out"
                                    :class="{ 'rotate-180': showCampusDropDown }" />
                            </div>
                            <div v-show="showCampusDropDown"
                                class="flex flex-col h-40 overflow-y-auto shadow-lg px-2 pt-2 absolute w-full bg-white border left-0 top-10">
                                <div v-for="(item, index) in filteredCampuses" :key="index"
                                    class="flex items-center mb-2">
                                    <input type="checkbox" :value="item.campus_code" :id="`academic-year-${index}`"
                                        v-model="filters.campus_code" class="mr-2" />
                                    <label :for="`academic-year-${index}`" class="text-sm">{{ item.campus_code
                                        }}</label>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Local or Non Local</label>
                        <select v-model="filters.local_or_non_local" class="w-full px-2 py-1 border outline-none">
                            <option value="">All</option>
                            <option value="Local">Local</option>
                            <option value="Non Local">Non Local</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-semibold text-gray-700 mb-1">Gender</label>
                        <select v-model="filters.gender" class="w-full px-2 py-1 border outline-none">
                            <option value="">All</option>
                            <option value="M">Male</option>
                            <option value="F">Female</option>
                        </select>
                    </div>
                </div>

                <!-- Apply Button -->
                <div class="p-6 flex justify-center space-x-6 border-t">
                    <button @click.stop="clearFilters"
                        class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-semibold">
                        Clear
                    </button>
                    <button @click.stop="applyFilters"
                        class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-sm font-semibold">
                        Apply
                    </button>
                </div>
            </aside>
        </transition>

        <div class="container mx-auto px-4 sm:px-8 py-6 space-y-8">
            <div class="flex justify-between items-center flex-wrap gap-4">
                <h1 class="text-2xl font-bold text-gray-800 flex items-center">
                    <font-awesome-icon :icon="['fas', 'chart-line']" class="text-blue-600 mr-2" />
                    <div class="uppercase">Student Dashboard ({{ filters.entity }})</div>
                </h1>
                <div class="flex items-center flex-wrap gap-4">
                    <div v-if="timeStamp" class="flex items-center space-x-2 text-sm text-gray-500 mt-2">
                        <font-awesome-icon :icon="['fas', 'clock']" class="text-gray-400 w-4 h-4" />
                        <span class="font-medium text-gray-700">Last Updated:</span>
                        <span>{{ timeStamp }}</span>
                    </div>
                </div>
            </div>

            <!-- Chart Grid -->
            <div ref="screenshotArea" class="w-full flex flex-col space-y-8">
                <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                    <div class="flex flex-col bg-white shadow-md p-6 justify-between">
                        <div
                            class="flex-1 mb-4 bg-gray-50 p-4 border border-gray-100 text-center flex flex-col justify-center">
                            <p class="text-2xl text-gray-600 flex items-center justify-center gap-1">
                                <font-awesome-icon :icon="['fas', 'users']" class="text-blue-600 text-2xl" />
                                <span class="text-2xl font-medium text-gray-700">Total Active Students</span>
                            </p>
                            <span class="text-6xl font-bold text-blue-900">{{
                                getFormatNumberInternational(totalActiveStudents)
                                }}</span>
                        </div>
                        <!-- Gender Breakdown -->
                        <div class="grid grid-cols-2 gap-4">
                            <!-- Male -->
                            <div class="bg-gray-50 p-4 border border-gray-100 text-center">
                                <p class="text-2xl text-gray-600 flex items-center justify-center gap-1">
                                    <font-awesome-icon :icon="['fas', 'male']" class="text-blue-600" />
                                    Male
                                </p>
                                <p class="text-4xl font-semibold text-blue-700 mt-1">{{
                                    getFormatNumberInternational(totalMaleCount) }}</p>
                            </div>
                            <!-- Female -->
                            <div class="bg-gray-50 p-4 border border-gray-100 text-center">
                                <p class="text-2xl text-gray-600 flex items-center justify-center gap-1">
                                    <font-awesome-icon :icon="['fas', 'female']" class="text-pink-500" />
                                    Female
                                </p>
                                <p class="text-4xl font-semibold text-pink-600 mt-1">{{
                                    getFormatNumberInternational(totalFemaleCount) }}</p>
                            </div>
                        </div>
                    </div>
                    <div class="bg-white p-6 shadow-md">
                        <h2 class="text-lg font-semibold text-gray-700 mb-2 flex items-center">
                            <span class="mr-2">🏛️</span>
                            <span>Students by entity</span>
                        </h2>
                        <apexchart type="bar" height="250" :options="studentsByEntityChartOptions"
                            :series="studentsByEntityChartSeries" />
                    </div>
                    <div class="bg-white p-6 shadow-md">
                        <h2 class="text-lg font-semibold text-gray-700 mb-2 flex items-center">
                            <span class="-mt-1 mr-2">👥</span>
                            <span>Students by gender</span>
                        </h2>
                        <apexchart class="flex justify-center items-center" type="donut" width="350"
                            :options="studentsByGenderChartOptions" :series="studentsByGenderChartSeries" />
                    </div>
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div class="bg-white p-6 shadow-md">
                        <h2 class="text-lg font-semibold text-gray-700 mb-2 flex items-center">
                            <span class="text-xl -mt-1 mr-2">🏫</span>
                            <span>Students by campus</span>
                        </h2>
                        <apexchart type="bar" height="400" :options="studentsByGenderCampusChartOptions"
                            :series="studentsByGenderCampusSeries"></apexchart>
                    </div>

                    <div class="md:col-span-2 bg-white p-6 shadow-md">
                        <h2 class="text-lg font-semibold text-gray-700 mb-2 flex items-center">
                            <span class="mr-2">📘</span>
                            <span>Gender distribution by program</span>
                        </h2>
                        <apexchart type="bar" height="600" :options="studentsByGenderProgramChartOptions"
                            :series="studentsByGenderProgramSeries" />
                    </div>

                    <StudentsByStudentClass :data="studentsByStudentClassData" />
                    <StudentsByGenderSponsor :data="studentsByGenderSponsorData" />
                    <NoOfExpatAndLocalAgainstProgramCampus class="md:col-span-2"
                        :data="noOfExpatAndLocalAgainstProgramCampusData" />
                    <StudentsByGpaGender :data="studentsByGpaBucketGenderData" />
                    <HighSchoolGpaBucketByGender :data="studentsByHighSchoolGpaBucketGenderData" />

                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    <AtsDistribution :data="atsDistributionData" />
                    <SchoolTypeDistribution :data="schoolTypeDistributionData" />
                    <LocationTypeDistribution :data="locationTypeDistributionData" />
                </div>

                <!-- Student Admission Details Section -->
                <div class="mt-8">
                    <div class="flex items-center mb-6">
                        <h2 class="text-2xl font-bold text-gray-800 flex items-center">
                            <font-awesome-icon :icon="['fas', 'clipboard-check']" class="text-indigo-600 mr-3" />
                            Student Admission Details
                        </h2>
                    </div>

                    <!-- Summary Tiles -->
                    <AdmissionDetailsSummary :data="studentAdmissionDetailsData" />

                    <!-- Charts Grid -->
                    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
                        <ApplicationStatusChart :data="getAdmissionData('applicationStatusBreakdown')" />
                        <ApplicationTypeChart :data="getAdmissionData('applicationTypeBreakdown')" />
                        <AdmissionTypeChart :data="getAdmissionData('admissionTypeBreakdown')" />
                        <TarasulStatusChart :data="getAdmissionData('tarasulStatusBreakdown')" />
                        <AcademicYearBreakdownChart :data="getAdmissionData('academicYearBreakdown')" />
                        <ProgramBreakdownChart :data="getAdmissionData('programBreakdown')" />
                    </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <SchoolStatistics class="sm:col-span-2 md:col-span-1" :data="schoolStatisticsData" />
                    <SchoolStatisticsByStream class="sm:col-span-2 md:col-span-1"
                        :data="schoolStatisticsByStreamData" />
                    <HighSchoolScoreVsCurrentGpa class="sm:col-span-2" :data="highSchoolScoreVsCurrentGpaData"
                        :paginator-info="highSchoolScoreVsCurrentGpapaginatorInfo"
                        :min-max-gpa-count="gpaExtremesWithCountData" :gpa-range-count="studentsByGpaBucketGenderData"
                        :min-max-hs-gpa-count="hsGpaExtremesWithCountData"
                        :hs-gpa-range-count="studentsByHighSchoolGpaBucketGenderData" @set-page="setPage" />
                    <GpaTrendsByYearOfStudy class="sm:col-span-2 lg:col-span-1" :data="gpaTrendsByYearOfStudyData" />
                    <AcademicProbationAndWarning class="sm:col-span-2 lg:col-span-1"
                        :data="academicProbationAndWarningsData" />
                    <GpaBetweenPrograms class="sm:col-span-2" :data="gpaBetweenProgramsData" />
                    <TopBottomGPA class="sm:col-span-2" :data="gpaBetweenProgramsData" />
                    <StudentOnHold class="sm:col-span-2" :data="studentsOnHoldData" />
                    <GraduationRatesByProgram class="sm:col-span-2" :data="graduationRatesByProgramData" />
                    <FailedCourse class="sm:col-span-2" :data="failedCoursesData" />
                    <RetentionRatesYearOnYearByProgram class="sm:col-span-2" :clear-inner-filters="clearInnerFilters"
                        :data="retentionRatesYearOnYearByProgramData"
                        @fetch-retention-rates-by-program="fetchRetentionRatesByProgram" />
                    <ProgramsWithHigherAttritionOrDropoutRates class="sm:col-span-2"
                        :clear-inner-filters="clearInnerFilters" :data="programsWithHigherAttritionOrDropoutRatesData"
                        @fetch-data="fetchProgramsWithHigherAttritionOrDropoutRates" />
                    <AverageGraduationTimeByProgram class="sm:col-span-2" :data="averageGraduationTimeByProgramData" />
                    <GraduationStatusByProgram class="sm:col-span-2" :data="graduationStatusByProgramData" />
                    <GpaPerformanceAfterRemedial class="sm:col-span-2 md:col-span-1"
                        :data="gpaPerformanceAfterRemedialData" />
                    <FoundationRemedialCountByStatus class="sm:col-span-2 md:col-span-1"
                        :data="foundationRemedialCountByStatusData" />
                </div>
            </div>
        </div>
        <div class="h-full flex justify-center items-center" v-if="isLoading">
            <base-page-spinner type="spin" class-list="h-20"></base-page-spinner>
        </div>
    </div>
</template>

<script>
import gql from 'graphql-tag';
import IconChevronDown from "../../components/icons/IconChevronDown.vue";
import { formattedDateWithTimeAmPm, formatNumberInternational } from "../../helpers/utils";
// import StudentsByGpa from './charts/StudentsByGpa.vue';
import StudentsByGpaGender from './charts/StudentsByGpaGender.vue';
import StudentsByStudentClass from './charts/StudentsByStudentClass.vue';
import StudentsByGenderSponsor from './charts/StudentsByGenderSponsor.vue';
import NoOfExpatAndLocalAgainstProgramCampus from './table/NoOfExpatAndLocalAgainstProgramCampus.vue';
import GpaBetweenPrograms from './charts/GpaBetweenPrograms.vue';
import GpaTrendsByYearOfStudy from './charts/GpaTrendsByYearOfStudy.vue';
import TopBottomGPA from './table/TopBottomGPA.vue';
import GraduationRatesByProgram from './table/GraduationRatesByProgram.vue';
import AcademicProbationAndWarning from './charts/AcademicProbationAndWarning.vue';
import StudentOnHold from './charts/StudentOnHold.vue';
import HighSchoolGpaBucketByGender from './charts/HighSchoolGpaBucketByGender.vue';
import HighSchoolScoreVsCurrentGpa from './table/HighSchoolScoreVsCurrentGpa.vue';
import FailedCourse from './charts/FailedCourse.vue';
import RetentionRatesYearOnYearByProgram from './charts/RetentionRatesYearOnYearByProgram.vue';
import ProgramsWithHigherAttritionOrDropoutRates from './charts/ProgramsWithHigherAttritionOrDropoutRates.vue';
import AverageGraduationTimeByProgram from './charts/AverageGraduationTimeByProgram.vue';
import GraduationStatusByProgram from './charts/GraduationStatusByProgram.vue';
import GpaPerformanceAfterRemedial from './charts/GpaPerformanceAfterRemedial.vue';
import FoundationRemedialCountByStatus from './charts/FoundationRemedialCountByStatus.vue';
import AtsDistribution from './charts/AtsDistribution.vue';
import SchoolTypeDistribution from './charts/SchoolTypeDistribution.vue';
import LocationTypeDistribution from './charts/LocationTypeDistribution.vue';
import SchoolStatistics from './charts/SchoolStatistics.vue';
import SchoolStatisticsByStream from './charts/SchoolStatisticsByStream.vue';
import AdmissionDetailsSummary from './summary/AdmissionDetailsSummary.vue';
import ApplicationStatusChart from './charts/student-admission-details/ApplicationStatusChart.vue';
import ApplicationTypeChart from './charts/student-admission-details/ApplicationTypeChart.vue';
import AdmissionTypeChart from './charts/student-admission-details/AdmissionTypeChart.vue';
import TarasulStatusChart from './charts/student-admission-details/TarasulStatusChart.vue';
import ProgramBreakdownChart from './charts/student-admission-details/ProgramBreakdownChart.vue';
import AcademicYearBreakdownChart from './charts/student-admission-details/AcademicYearBreakdownChart.vue';

const STUDENTS_STATISTICS_QUERY = gql`
  query StudentsStatistics($entity: String, $campus_code: [String], $local_or_non_local: String, $gender: String) {
    studentsStatistics(entity: $entity, campus_code: $campus_code, local_or_non_local: $local_or_non_local, gender: $gender) {
      error
      error_code
      message
      data {
        studentsByEntity {
            entity
            count
        }
        studentsCount
        studentsByGender {
            gender
            count
        }
        studentsByGenderCampus {
            campus_code
            male_count
            female_count
        }
        studentsByGenderProgram {
            program_name
            male_count
            female_count
        }
        studentsByGpaBucketGender {
            gpa_bucket
            male_count
            female_count
        }
        studentsByStudentClass {
            student_class
            male_count
            female_count
        }
        studentsByGenderSponsor {
            sponsor
            male_count
            female_count
        }
        noOfExpatAndLocalAgainstProgramCampus {
            program_name
            campus_code
            national_count
            expat_count
        }
        gpaBetweenPrograms {
            program_name
            student_count
            average_gpa
            min_gpa
            max_gpa
        }
        gpaTrendsByYearOfStudy {
            year_of_study
            student_count
            average_gpa
            min_gpa
            max_gpa
        }
        graduationRatesByProgram {
            program_name
            total_students
            graduates
            graduation_rate
        }
        academicProbationAndWarnings {
            label
            count
        }
        studentsOnHold {
            label
            count
        }
        studentsByHighSchoolGpaBucketGender {
            gpa_bucket
            male_count
            female_count
        }
        failedCourses {
            label
            count
        }
        retentionRatesYearOnYearByProgram {
            program_name
            year1_retention_rate
            year2_retention_rate
            year3_retention_rate
            year4_retention_rate
            year5_retention_rate
        }
        programsWithHigherAttritionOrDropoutRates {
            program_name
            attrition_rate
        }
        averageGraduationTimeByProgram {
            label
            average
        }
        graduationStatusByProgram {
            label
            on_time_graduation
            delayed_graduation
            on_time_percentage
            delayed_percentage
        }
        gpaPerformanceAfterRemedial {
            label
            avg_gpa
            student_count
        }
        gpaExtremesWithCount {
            gpa
            count
        }
        hsGpaExtremesWithCount {
            hs_gpa
            count
        }
        foundationRemedialCountByStatus {
            student_status_name
            foundation_count
            remedial_count
        }
        atsDistribution {
            label
            count
        }
        schoolTypeDistribution {
            label
            count
        }
        locationTypeDistribution {
            label
            count
        }
        schoolStatistics {
            school_name
            total_students
            average_gpa
        }
        schoolStatisticsByStream {
            stream
            total_students
            average_gpa
        }
        studentAdmissionDetails {
            summary {
                total_applications
                accepted_applications
                rejected_applications
                md_approved
                admission_rejected
                interview_approved
                admission_returned
                new_applications
                transfer_applicants
                napo_applicants
                direct_applicants
                bridging_students
                direct_entry
                conditional_admission
                tarasul_approved
                tarasul_pending
                tarasul_rejected
                male_applications
                female_applications
                local_applications
                non_local_applications
            }
            applicationStatusBreakdown {
                application_status
                count
            }
            applicationTypeBreakdown {
                application_type
                count
            }
            admissionTypeBreakdown {
                admission_type
                count
            }
            tarasulStatusBreakdown {
                tarasul_status
                count
            }
            programBreakdown {
                program_name
                count
            }
            campusBreakdown {
                campus_code
                count
            }
            academicYearBreakdown {
                academic_year
                count
            }
        }
      }
    }
  }
`;

const HIGH_SCHOOL_GPA_VS_CURRENT_GPA_QUERY = gql`
  query GetHighSchoolScoreVsCurrentGpa($first: Int, $page: Int, $entity: String, $campus_code: [String], $local_or_non_local: String, $gender: String) {
    getHighSchoolScoreVsCurrentGpa(first: $first, page: $page, entity: $entity, campus_code: $campus_code, local_or_non_local: $local_or_non_local, gender: $gender) {
      error
      error_code
      message
      data {
        student_id
        name_en
        gpa
        program
        hs_name
        hs_score
      }
      paginatorInfo {
        count
        currentPage
        firstItem
        hasMorePages
        lastItem
        lastPage
        perPage
        total
      }
    }
  }
`;

const FETCH_RETENTION_RATES_BY_PROGRAM = gql`
  query FetchRetentionRatesByProgram($entity: String, $admission_year_term: String) {
    fetchRetentionRatesByProgram(entity: $entity, admission_year_term: $admission_year_term) {
      error
      error_code
      message
      data {
        retentionRatesYearOnYearByProgram {
            program_name
            year1_retention_rate
            year2_retention_rate
            year3_retention_rate
            year4_retention_rate
            year5_retention_rate
        }
      }
    }
  }
`;

const FETCH_PROGRAMS_WITH_HIGHER_ATTRITION_RATES = gql`
  query FetchProgramsWithHigherAttritionOrDropoutRates($entity: String, $admission_year_term: String) {
    fetchProgramsWithHigherAttritionOrDropoutRates(entity: $entity, admission_year_term: $admission_year_term) {
      error
      error_code
      message
      data {
        programsWithHigherAttritionOrDropoutRates {
            program_name
            attrition_rate
        }
      }
    }
  }
`;

export default {
    name: "student-dashboard-index",
    components: {
        IconChevronDown,
        // StudentsByGpa,
        StudentsByGpaGender,
        StudentsByStudentClass,
        StudentsByGenderSponsor,
        NoOfExpatAndLocalAgainstProgramCampus,
        GpaBetweenPrograms,
        GpaTrendsByYearOfStudy,
        TopBottomGPA,
        GraduationRatesByProgram,
        AcademicProbationAndWarning,
        StudentOnHold,
        HighSchoolGpaBucketByGender,
        HighSchoolScoreVsCurrentGpa,
        FailedCourse,
        RetentionRatesYearOnYearByProgram,
        ProgramsWithHigherAttritionOrDropoutRates,
        AverageGraduationTimeByProgram,
        GraduationStatusByProgram,
        GpaPerformanceAfterRemedial,
        FoundationRemedialCountByStatus,
        AtsDistribution,
        SchoolTypeDistribution,
        LocationTypeDistribution,
        SchoolStatistics,
        SchoolStatisticsByStream,
        AdmissionDetailsSummary,
        ApplicationStatusChart,
        ApplicationTypeChart,
        AdmissionTypeChart,
        TarasulStatusChart,
        ProgramBreakdownChart,
        AcademicYearBreakdownChart,
    },
    data() {
        return {
            isLoading: false,
            flipped: true,
            showFilter: false,
            filters: {
                entity: "FCHS",
                campus_code: [],
                local_or_non_local: "",
                gender: "",
            },
            clearInnerFilters: false,
            first: 10,
            page: 1,
            activeDropDown: "",
            academicYearsList: [],
            campusList: [],
            showCampusDropDown: false,
            totalActiveStudents: 0,
            totalMaleCount: 0,
            totalFemaleCount: 0,
            studentsByEntityChartOptions: {
                chart: {
                    toolbar: { show: false },
                    events: {
                        dataPointSelection: (event, chartContext, config) => {
                            const selectedEntity = chartContext.w.config.xaxis.categories[config.dataPointIndex];
                            if (selectedEntity) {
                                this.onEntityBarClick(selectedEntity);
                            }
                        },
                    },
                },
                xaxis: {
                    categories: [],
                },
                colors: ["#2F5597"],
                plotOptions: {
                    bar: {
                        borderRadius: 0,
                        columnWidth: "40%",
                    },
                },
                tooltip: {
                    y: {
                        formatter: (val) => `${val} students`,
                    },
                },
            },
            studentsByEntityChartSeries: [
                {
                    name: "Active Students",
                    data: []
                }
            ],
            studentsByGenderChartOptions: {
                labels: [],
                colors: [],
                legend: {
                    position: 'bottom',
                    formatter: (seriesName, opts) => {
                        const seriesIndex = opts.seriesIndex;
                        const value = this.studentsByGenderChartSeries[seriesIndex];
                        return `${seriesName} - ${value}`;
                    },
                },
                dataLabels: {
                    enabled: true,
                    formatter: function (val) {
                        return val.toFixed(1) + '%';
                    },
                },
                tooltip: {
                    y: {
                        formatter: (val) => `${val} Students`,
                    },
                },
                chart: {
                    type: 'donut',
                    events: {
                        dataPointSelection: (event, chartContext, config) => {
                            const selectedLabel = chartContext.w.config.labels[config.dataPointIndex];
                            if (selectedLabel) {
                                this.onGenderSegmentClick(selectedLabel);
                            }
                        },
                    },
                },
            },
            studentsByGenderChartSeries: [],
            studentsByGenderCampusChartOptions: {
                chart: {
                    type: 'bar',
                    stacked: true,
                    toolbar: { show: false },
                },
                plotOptions: {
                    bar: {
                        horizontal: true,
                        barHeight: '60%',
                        dataLabels: {
                            total: {
                                enabled: true,
                                offsetX: 0,
                                style: {
                                    fontSize: '13px',
                                    fontWeight: 900
                                }
                            }
                        }
                    },
                },
                dataLabels: {
                    enabled: true,
                    offsetX: 0,
                    style: {
                        fontSize: '10px',
                        fontWeight: 900
                    }
                },
                stroke: {
                    width: 1,
                    colors: ['#fff']
                },
                xaxis: {
                    categories: [],
                    title: {
                        text: 'Number of Students',
                        style: {
                            fontSize: '12px',
                            fontWeight: 'bold',
                        },
                    },
                },
                colors: ['#4C78A8', '#F58518'],
                legend: {
                    position: 'top',
                    horizontalAlign: 'right',
                    labels: {
                        colors: '#4A5568',
                    },
                },
                tooltip: {
                    shared: true,
                    intersect: false,
                },
            },
            studentsByGenderCampusSeries: [
                {
                    name: 'Male',
                    data: [],
                },
                {
                    name: 'Female',
                    data: [],
                },
            ],
            studentsByGenderProgramChartOptions: {
                chart: {
                    type: 'bar',
                    stacked: true,
                    toolbar: { show: false },
                },
                plotOptions: {
                    bar: {
                        horizontal: true,
                        barHeight: '60%',
                        dataLabels: {
                            total: {
                                enabled: true,
                                offsetX: 0,
                                style: {
                                    fontSize: '13px',
                                    fontWeight: 900
                                }
                            }
                        }
                    },
                },
                dataLabels: {
                    enabled: true,
                    offsetX: 0,
                    style: {
                        fontSize: '10px',
                        fontWeight: 900
                    }
                },
                stroke: {
                    width: 1,
                    colors: ['#fff']
                },
                xaxis: {
                    categories: [],
                    title: {
                        text: 'Number of Students',
                        style: {
                            fontSize: '12px',
                            fontWeight: 'bold',
                        },
                    },
                },
                colors: ['#4C78A8', '#F58518'],
                legend: {
                    position: 'top',
                    horizontalAlign: 'right',
                    labels: {
                        colors: '#4A5568',
                    },
                },
                tooltip: {
                    shared: true,
                    intersect: false,
                },
            },
            studentsByGenderProgramSeries: [
                {
                    name: 'Male',
                    data: [],
                },
                {
                    name: 'Female',
                    data: [],
                },
            ],
            // studentsByGpaBucketData: [],
            studentsByGpaBucketGenderData: [],
            studentsByStudentClassData: [],
            studentsByGenderSponsorData: [],
            noOfExpatAndLocalAgainstProgramCampusData: [],
            gpaBetweenProgramsData: [],
            gpaTrendsByYearOfStudyData: [],
            graduationRatesByProgramData: [],
            academicProbationAndWarningsData: [],
            studentsOnHoldData: [],
            studentsByHighSchoolGpaBucketGenderData: [],
            highSchoolScoreVsCurrentGpaData: [],
            highSchoolScoreVsCurrentGpapaginatorInfo: [],
            failedCoursesData: [],
            retentionRatesYearOnYearByProgramData: [],
            programsWithHigherAttritionOrDropoutRatesData: [],
            averageGraduationTimeByProgramData: [],
            graduationStatusByProgramData: [],
            gpaPerformanceAfterRemedialData: [],
            gpaExtremesWithCountData: [],
            hsGpaExtremesWithCountData: [],
            foundationRemedialCountByStatusData: [],
            atsDistributionData: [],
            schoolTypeDistributionData: [],
            locationTypeDistributionData: [],
            schoolStatisticsData: [],
            schoolStatisticsByStreamData: [],
            studentAdmissionDetailsData: {},
        };
    },
    created() {
        this.getCampusList();
    },
    mounted() {
        this.loadChartData();
    },
    watch: {
        showCampusDropDown(newVal) {
            if (newVal) {
                this.activeDropDown = 'campus';
                this.collapseDropDown('campus');
            }
        },
    },
    computed: {
        filteredCampuses() {
            if (this.filters.entity) {
                return this.campusList.filter(
                    (campus) => campus.entity === this.filters.entity
                );
            } else {
                return this.campusList;
            }
        },
        currentYear() {
            const settings = this.$store.getters["settings/settings"] || [];
            const current_year = settings.find(setting => setting.name === "current_year");
            return current_year ? current_year.value : new Date().getFullYear();
        },
        timeStamp() {
            const settings = this.$store.getters["settings/settings"] || [];
            const lastUpdated = settings.find(setting => setting.name === "last_updated");
            if (lastUpdated && lastUpdated.value) {
                return formattedDateWithTimeAmPm(lastUpdated.value);
            }
            return "";
        },
    },
    methods: {
        toggleFlip() {
            this.flipped = !this.flipped;
        },
        toggleFilter() {
            this.showFilter = !this.showFilter;
        },
        clearFilters() {
            this.toggleFilter();
            this.filters = {
                entity: "FCHS",
                campus_code: [],
                local_or_non_local: "",
                gender: "",
            };
            this.clearInnerFilters = true;
            this.collapseDropDown();
            this.loadChartData();
        },
        applyFilters() {
            this.toggleFilter();
            this.loadChartData();
        },
        async loadChartData() {
            this.isLoading = true;
            try {
                const response = await this.$apollo.query({
                    query: STUDENTS_STATISTICS_QUERY,
                    variables: {
                        entity: this.filters.entity,
                        campus_code: this.filters.campus_code,
                        local_or_non_local: this.filters.local_or_non_local,
                        gender: this.filters.gender,
                    },
                    fetchPolicy: 'network-only'
                });

                const data = response.data.studentsStatistics.data;

                // Fetch high school vs current gpa data
                this.getHighSchoolScoreVsCurrentGpa();

                this.totalActiveStudents = data.studentsCount;

                this.studentsByEntityChartOptions = {
                    ...this.studentsByEntityChartOptions,
                    xaxis: {
                        ...this.studentsByEntityChartOptions.xaxis,
                        categories: data.studentsByEntity.map(e => e.entity)
                    }
                };
                this.studentsByEntityChartSeries[0].data = data.studentsByEntity.map(e => e.count);

                const studentsByGenderLabels = [];
                const studentsByGenderSeries = [];
                const studentsByGenderColors = [];
                let maleCount = 0;
                let femaleCount = 0;
                data.studentsByGender.forEach(item => {
                    studentsByGenderLabels.push(item.gender);
                    studentsByGenderSeries.push(item.count);
                    studentsByGenderColors.push(item.gender === 'Male' ? '#4C78A8' : '#F58518');
                    if (item.gender === 'Male') {
                        maleCount = item.count;
                    } else if (item.gender === 'Female') {
                        femaleCount = item.count;
                    }
                });
                this.studentsByGenderChartOptions = {
                    ...this.studentsByGenderChartOptions,
                    labels: studentsByGenderLabels,
                    colors: studentsByGenderColors,
                };
                this.studentsByGenderChartSeries = studentsByGenderSeries;
                this.totalMaleCount = maleCount;
                this.totalFemaleCount = femaleCount;

                const studentsByGenderCampusCategories = data.studentsByGenderCampus.map(item => item.campus_code);
                const studentsByGenderCampusMaleData = data.studentsByGenderCampus.map(item => item.male_count);
                const studentsByGenderCampusFemaleData = data.studentsByGenderCampus.map(item => item.female_count);
                this.studentsByGenderCampusChartOptions = {
                    ...this.studentsByGenderCampusChartOptions,
                    xaxis: {
                        ...this.studentsByGenderCampusChartOptions.xaxis,
                        categories: studentsByGenderCampusCategories,
                    },
                };
                this.studentsByGenderCampusSeries = [
                    {
                        name: 'Male',
                        data: studentsByGenderCampusMaleData,
                    },
                    {
                        name: 'Female',
                        data: studentsByGenderCampusFemaleData,
                    },
                ];

                const studentsByGenderProgramCategories = data.studentsByGenderProgram.map(item => item.program_name);
                const studentsByGenderProgramMaleData = data.studentsByGenderProgram.map(item => item.male_count);
                const studentsByGenderProgramFemaleData = data.studentsByGenderProgram.map(item => item.female_count);
                this.studentsByGenderProgramChartOptions = {
                    ...this.studentsByGenderProgramChartOptions,
                    xaxis: {
                        ...this.studentsByGenderProgramChartOptions.xaxis,
                        categories: studentsByGenderProgramCategories,
                    },
                };
                this.studentsByGenderProgramSeries = [
                    {
                        name: 'Male',
                        data: studentsByGenderProgramMaleData,
                    },
                    {
                        name: 'Female',
                        data: studentsByGenderProgramFemaleData,
                    },
                ];

                // this.studentsByGpaBucketData = data.studentsByGpaBucket;
                this.studentsByGpaBucketGenderData = data.studentsByGpaBucketGender;
                this.studentsByStudentClassData = data.studentsByStudentClass;
                this.studentsByGenderSponsorData = data.studentsByGenderSponsor;
                this.noOfExpatAndLocalAgainstProgramCampusData = data.noOfExpatAndLocalAgainstProgramCampus;
                this.gpaBetweenProgramsData = data.gpaBetweenPrograms;
                this.gpaTrendsByYearOfStudyData = data.gpaTrendsByYearOfStudy;
                this.graduationRatesByProgramData = data.graduationRatesByProgram;
                this.academicProbationAndWarningsData = data.academicProbationAndWarnings;
                this.studentsOnHoldData = data.studentsOnHold;
                this.studentsByHighSchoolGpaBucketGenderData = data.studentsByHighSchoolGpaBucketGender;
                this.failedCoursesData = data.failedCourses;
                this.retentionRatesYearOnYearByProgramData = data.retentionRatesYearOnYearByProgram;
                this.programsWithHigherAttritionOrDropoutRatesData = data.programsWithHigherAttritionOrDropoutRates;
                this.averageGraduationTimeByProgramData = data.averageGraduationTimeByProgram;
                this.graduationStatusByProgramData = data.graduationStatusByProgram;
                this.gpaPerformanceAfterRemedialData = data.gpaPerformanceAfterRemedial;
                this.gpaExtremesWithCountData = data.gpaExtremesWithCount;
                this.hsGpaExtremesWithCountData = data.hsGpaExtremesWithCount;
                this.foundationRemedialCountByStatusData = data.foundationRemedialCountByStatus;
                this.atsDistributionData = data.atsDistribution;
                this.schoolTypeDistributionData = data.schoolTypeDistribution;
                this.locationTypeDistributionData = data.locationTypeDistribution;
                this.schoolStatisticsData = data.schoolStatistics;
                this.schoolStatisticsByStreamData = data.schoolStatisticsByStream;
                this.studentAdmissionDetailsData = data.studentAdmissionDetails;

            } catch (error) {
                console.error("Failed to load students statistics:", error);
            }
            this.isLoading = false;
        },
        async getCampusList() {
            try {
                const { data } = await this.$apollo.query({
                    query: gql`
                        query GetCampusList($student_status: String, $entity: String) {
                            getCampusList(student_status: $student_status, entity: $entity) {
                                entity
                                campus_code
                            }
                        }
                    `,
                    variables: {
                        student_status: "IG",
                        entity: "",
                    },
                    fetchPolicy: "network-only",
                });
                this.campusList = data.getCampusList;
            } catch (error) {
                console.log(error);
            }
        },
        collapseDropDown(dropDownName = '') {
            if (dropDownName) {
                if (dropDownName != 'campus') {
                    this.showCampusDropDown = false;
                }
            } else {
                this.showCampusDropDown = false;
            }
        },
        getFormatNumberInternational(number) {
            return formatNumberInternational(parseInt(number));
        },
        onEntityBarClick(entity) {
            this.filters.entity = entity;
            this.loadChartData();
        },
        onGenderSegmentClick(gender) {
            const genderMap = {
                Male: 'M',
                Female: 'F',
            };

            const selectedGender = genderMap[gender];
            if (!selectedGender) return;

            this.filters.gender = selectedGender;
            this.loadChartData();
        },
        async getHighSchoolScoreVsCurrentGpa() {
            try {
                const response = await this.$apollo.query({
                    query: HIGH_SCHOOL_GPA_VS_CURRENT_GPA_QUERY,
                    variables: {
                        first: this.first,
                        page: this.page,
                        entity: this.filters.entity,
                        campus_code: this.filters.campus_code,
                        local_or_non_local: this.filters.local_or_non_local,
                        gender: this.filters.gender,
                    },
                    fetchPolicy: 'network-only'
                });

                this.highSchoolScoreVsCurrentGpaData = response.data.getHighSchoolScoreVsCurrentGpa.data;
                this.highSchoolScoreVsCurrentGpapaginatorInfo = response.data.getHighSchoolScoreVsCurrentGpa.paginatorInfo;

            } catch (error) {
                console.error("Failed to load high school gpa vs current gpa:", error);
            }
        },
        setPage(page) {
            this.page = page;
            this.getHighSchoolScoreVsCurrentGpa();
        },
        async fetchRetentionRatesByProgram(admissionYear) {
            this.clearInnerFilters = false;
            try {
                const response = await this.$apollo.query({
                    query: FETCH_RETENTION_RATES_BY_PROGRAM,
                    variables: {
                        entity: this.filters.entity,
                        admission_year_term: admissionYear,
                    },
                    fetchPolicy: 'network-only'
                });

                this.retentionRatesYearOnYearByProgramData = response.data.fetchRetentionRatesByProgram.data.retentionRatesYearOnYearByProgram;

            } catch (error) {
                console.error("Failed to fetch retention rates by program:", error);
            }
        },
        async fetchProgramsWithHigherAttritionOrDropoutRates(admissionYear) {
            this.clearInnerFilters = false;
            try {
                const response = await this.$apollo.query({
                    query: FETCH_PROGRAMS_WITH_HIGHER_ATTRITION_RATES,
                    variables: {
                        entity: this.filters.entity,
                        admission_year_term: admissionYear,
                    },
                    fetchPolicy: 'network-only'
                });

                this.programsWithHigherAttritionOrDropoutRatesData = response.data.fetchProgramsWithHigherAttritionOrDropoutRates.data.programsWithHigherAttritionOrDropoutRates;

            } catch (error) {
                console.error("Failed to fetch retention rates by program:", error);
            }
        },
        getAdmissionData(field) {
            if (this.studentAdmissionDetailsData && this.studentAdmissionDetailsData[field]) {
                const data = this.studentAdmissionDetailsData[field];
                // Convert null count values to 0
                if (Array.isArray(data)) {
                    return data.map(item => ({
                        ...item,
                        count: item.count === null || item.count === undefined ? 0 : item.count
                    }));
                }
                return data;
            }
            return [];
        }
    },
};
</script>

<style lang="scss" scoped>
.slide-enter-active,
.slide-leave-active {
    transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
    transform: translateX(-100%);
}
</style>